/**
 * iter_196 — Admin Community Control Center.
 *
 * Lets platform admins moderate Crews / Groups / Clubs across the whole
 * platform. Supports:
 *   • Filters: Active · Archived · Abandoned · All
 *   • Archive / Restore / Delete Permanently
 *   • View full audit trail per community
 *
 * Backed by GET /api/admin/crews and /api/admin/crews/{id}/audit. Both
 * are admin-only (403 for others).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import BackButton from "@/components/BackButton";
import CrewModerationModal from "@/components/CrewModerationModal";
import { optimizeImage } from "@/lib/mediaUrls";

const FILTERS = [
  { id: "active", label: "Active" },
  { id: "archived", label: "Archived" },
  { id: "abandoned", label: "Abandoned" },
  { id: "all", label: "All" },
];

const AdminCommunityPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [filter, setFilter] = useState("active");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modCrew, setModCrew] = useState(null);
  const [modMode, setModMode] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        if (r.data?.role !== "admin" && r.data?.role !== "moderator") {
          toast.error("Admin access required");
          navigate("/dashboard");
        }
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/crews?filter=${filter}&limit=200`);
      setRows(r.data?.crews || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load communities");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const openAudit = async (crew) => {
    setAuditFor(crew);
    setAuditLoading(true);
    setAuditRows([]);
    try {
      const r = await api.get(`/admin/crews/${crew.crew_id}/audit?limit=200`);
      setAuditRows(r.data?.audit || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit");
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080c] text-white pb-16">
      <SharedNavigation currentUser={user} activePage="admin" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <BackButton fallback="/admin/control-center" testId="admin-community-back" />
        <div className="mt-4 flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">
              🛡️ Community Control Center
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Archive · Restore · Delete Crews / Groups / Clubs. All actions are audited.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="admin-community-refresh"
            className="text-sm px-3 py-1.5 rounded-full border border-purple-400/40 text-purple-200 bg-purple-500/10 hover:bg-purple-500/25 disabled:opacity-50 transition"
          >
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mt-4" data-testid="admin-community-filters">
          {FILTERS.map((f) => {
            const isActive = f.id === filter;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`admin-community-filter-${f.id}`}
                aria-pressed={isActive}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  isActive
                    ? "bg-purple-500 border-purple-300 text-white"
                    : "bg-white/5 border-white/10 text-gray-300 hover:bg-purple-500/20"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Rows */}
        <div className="mt-6 space-y-3" data-testid="admin-community-list">
          {loading && rows.length === 0 && (
            <div className="text-gray-500 text-sm py-8 text-center">Loading…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-gray-500 text-sm py-8 text-center" data-testid="admin-community-empty">
              Nothing in this bucket.
            </div>
          )}
          {rows.map((c) => (
            <Card
              key={c.crew_id}
              className="bg-white/5 border-white/10 p-4 flex flex-wrap items-center gap-3"
              data-testid={`admin-community-row-${c.crew_id}`}
            >
              {c.cover_image ? (
                <img src={optimizeImage(c.cover_image, 'avatar')} alt={c.name} className="w-14 h-14 rounded-lg object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-purple-700 to-pink-700 flex items-center justify-center text-xl">
                  {c.kind === "group" ? "👥" : c.kind === "club" ? "🎵" : "🤝"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/crews/${c.crew_id}`}
                    className="font-bold text-white truncate hover:text-purple-300"
                  >
                    {c.name}
                  </Link>
                  <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/20 bg-white/5 uppercase tracking-wider">
                    {c.kind || "crew"}
                  </span>
                  {c.archived_at && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-100 border border-amber-400/40">
                      Archived
                    </span>
                  )}
                  {c.is_abandoned && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-100 border border-rose-400/40">
                      Abandoned
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {c.member_count || 0} members · {c.visibility || "public"} · Created {c.created_at?.slice(0, 10) || "—"}
                  {c.archived_at && ` · Archived ${c.archived_at.slice(0, 10)}`}
                  {c.archived_reason && ` — ${c.archived_reason}`}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {c.archived_at ? (
                  <Button
                    size="sm"
                    onClick={() => { setModCrew(c); setModMode("restore"); }}
                    data-testid={`admin-community-restore-${c.crew_id}`}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white h-7"
                  >
                    ↺ Restore
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => { setModCrew(c); setModMode("archive"); }}
                    data-testid={`admin-community-archive-${c.crew_id}`}
                    className="bg-amber-500 hover:bg-amber-600 text-black h-7"
                  >
                    📦 Archive
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => { setModCrew(c); setModMode("delete_permanent"); }}
                  data-testid={`admin-community-delete-${c.crew_id}`}
                  className="bg-rose-600 hover:bg-rose-700 text-white h-7"
                >
                  🗑️ Delete
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openAudit(c)}
                  data-testid={`admin-community-audit-${c.crew_id}`}
                  className="border-white/20 text-gray-200 hover:bg-white/10 h-7"
                >
                  📜 Audit
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Moderation modal */}
      {modCrew && (
        <CrewModerationModal
          open={!!modMode}
          mode={modMode || "archive"}
          crew={modCrew}
          onClose={() => { setModCrew(null); setModMode(null); }}
          onDone={() => { setModCrew(null); setModMode(null); load(); }}
        />
      )}

      {/* Audit drawer (simple inline modal) */}
      {auditFor && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="admin-community-audit-modal"
          onClick={() => setAuditFor(null)}
        >
          <div
            className="bg-gray-950 border border-white/10 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-white/10">
              <h3 className="font-bold text-white">Audit trail · {auditFor.name}</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Every archive, restore, delete, pin, or post-delete on this community.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {auditLoading && <div className="text-gray-500 text-sm">Loading…</div>}
              {!auditLoading && auditRows.length === 0 && (
                <div className="text-gray-500 text-sm">No audit entries yet.</div>
              )}
              <ul className="space-y-2">
                {auditRows.map((a) => (
                  <li
                    key={a.audit_id}
                    className="text-xs bg-white/5 border border-white/10 rounded px-3 py-2"
                    data-testid={`admin-community-audit-row-${a.audit_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-purple-200 uppercase">{a.action}</span>
                      <span className="text-gray-500">·</span>
                      <span className="text-gray-300">{a.actor_role || "user"} {a.actor_user_id?.slice(-6)}</span>
                      <span className="text-gray-500 ml-auto">{a.created_at?.replace("T", " ").slice(0, 19)}</span>
                    </div>
                    {a.reason && <p className="text-[11px] text-amber-200/80 mt-1">&ldquo;{a.reason}&rdquo;</p>}
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 py-3 border-t border-white/10 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAuditFor(null)}
                data-testid="admin-community-audit-close"
                className="border-white/20 text-gray-200 hover:bg-white/10"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommunityPage;
